import getSubscriptionStatus from "@helpers/getSubscriptionStatus"
import {
  setRevenueCatCustomerEmail,
  signInWithPassword,
} from "@helpers/supabase"
import { redirect } from "react-router-dom"

export interface SignInActionData {
  error: SignInActionError | null
}

export interface SignInActionError {
  code: string | undefined
  message: string
}

/**
 * Checks the form before calling Supabase, which answers an empty email with
 * "AuthApiError: missing email or phone" (Sentry PRODUCTION-3): a user
 * pressing "Log In" before filling in the form, not an error to report.
 */
function validateSignInForm({
  email,
  password,
}: {
  email: string
  password: string
}): SignInActionError | null {
  if (!email) {
    return { code: "validation_failed", message: "missing email" }
  }

  if (!password) {
    return { code: "missing_password", message: "missing password" }
  }

  return null
}

export default async function signInAction({
  request,
}: {
  request: Request
}): Promise<SignInActionData | Response> {
  const formData = await request.formData()
  // NOTE: Trimmed, as a pasted or autofilled email may have spaces around it.
  const email = String(formData.get("email") ?? "").trim()
  const password = String(formData.get("password") ?? "")

  const invalid = validateSignInForm({ email, password })

  if (invalid) return { error: invalid }

  const { data, error } = await signInWithPassword({
    email,
    password,
  })

  if (error) {
    return { error: { code: error.code, message: error.message } }
  }

  await setRevenueCatCustomerEmail()

  const url = new URL(request.url)
  const searchParams = new URLSearchParams(url.search)
  const path = searchParams.get("path")

  if (path) {
    return redirect(path)
  }

  const userId = data?.user?.id

  const subscriptionStatus = await getSubscriptionStatus(userId)

  switch (subscriptionStatus) {
    case "active": {
      return redirect("/account/subscription")
    }
    case "inactive": {
      return redirect("/checkout/plans")
    }
    case "none": {
      const url = new URL(request.url)
      const path = url.searchParams.get("path") ?? "/"

      return redirect(path)
    }
    default: {
      throw new Error(
        `Unhandled subscription status: ${subscriptionStatus as string}`,
      )
    }
  }
}
